import { useCallback, useState } from "react";
import type { TableViewTogglesViewType } from "@rio-cloud/rio-uikit/TableViewToggles";

export const TABLE_SETTINGS_STORAGE_PREFIX = "tco:table-settings:";

type ColumnDetailsMap = Record<string, object>;

export interface StoredTableSettings<TDetails extends ColumnDetailsMap> {
  columnOrder: string[];
  hiddenColumns: string[];
  columnsDetails: TDetails;
  viewType: TableViewTogglesViewType;
}

export interface UseStoredTableSettingsOptions<TDetails extends ColumnDetailsMap> {
  storageKey: string;
  defaultColumnOrder: string[];
  defaultHiddenColumns?: string[];
  defaultColumnsDetails?: TDetails;
  defaultViewType?: TableViewTogglesViewType;
  forcedViewType?: TableViewTogglesViewType;
}

const VIEW_TYPES: TableViewTogglesViewType[] = ["TABLE", "SINGLE_CARD", "MULTI_CARDS"];

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const isViewType = (value: unknown): value is TableViewTogglesViewType =>
  VIEW_TYPES.includes(value as TableViewTogglesViewType);

const readStored = <TDetails extends ColumnDetailsMap>(key: string): Partial<StoredTableSettings<TDetails>> | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeStored = <TDetails extends ColumnDetailsMap>(key: string, settings: StoredTableSettings<TDetails>) => {
  try {
    localStorage.setItem(key, JSON.stringify(settings));
  } catch {
    return;
  }
};

const reconcile = <TDetails extends ColumnDetailsMap>(
  stored: Partial<StoredTableSettings<TDetails>> | null,
  {
    defaultColumnOrder,
    defaultHiddenColumns = [],
    defaultColumnsDetails = {} as TDetails,
    defaultViewType = "TABLE",
    forcedViewType,
  }: UseStoredTableSettingsOptions<TDetails>
): StoredTableSettings<TDetails> => {
  const known = new Set(defaultColumnOrder);
  const storedOrder = isStringArray(stored?.columnOrder) ? stored?.columnOrder.filter((col) => known.has(col)) : null;
  const newColumns = storedOrder ? defaultColumnOrder.filter((col) => !storedOrder.includes(col)) : [];
  const columnOrder = storedOrder ? [...storedOrder, ...newColumns] : [...defaultColumnOrder];

  const storedHidden = isStringArray(stored?.hiddenColumns)
    ? stored?.hiddenColumns.filter((col) => known.has(col))
    : null;
  const hiddenColumns = storedHidden
    ? [...storedHidden, ...newColumns.filter((col) => defaultHiddenColumns.includes(col))]
    : [...defaultHiddenColumns];

  const storedDetails = stored?.columnsDetails;
  const columnsDetails =
    storedDetails && typeof storedDetails === "object"
      ? (Object.fromEntries(Object.entries(storedDetails).filter(([col]) => known.has(col))) as TDetails)
      : { ...defaultColumnsDetails };

  const storedViewType = stored?.viewType;
  const viewType = forcedViewType ?? (isViewType(storedViewType) ? storedViewType : defaultViewType);

  return { columnOrder, hiddenColumns, columnsDetails, viewType };
};

export const useStoredTableSettings = <TDetails extends ColumnDetailsMap = ColumnDetailsMap>(
  options: UseStoredTableSettingsOptions<TDetails>
) => {
  const storageKey = `${TABLE_SETTINGS_STORAGE_PREFIX}${options.storageKey}`;
  const [settings, setSettings] = useState<StoredTableSettings<TDetails>>(() =>
    reconcile(readStored<TDetails>(storageKey), options)
  );

  const update = useCallback(
    (patch: Partial<StoredTableSettings<TDetails>>) => {
      setSettings((current) => {
        const next = { ...current, ...patch };
        writeStored(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  const handleColumnChange = useCallback(
    (columnOrder: string[], hiddenColumns: string[], columnsDetails?: TDetails) =>
      update(columnsDetails ? { columnOrder, hiddenColumns, columnsDetails } : { columnOrder, hiddenColumns }),
    [update]
  );

  const handleColumnDetailsChange = useCallback(
    (column: string, details: TDetails[string]) =>
      setSettings((current) => {
        const next = { ...current, columnsDetails: { ...current.columnsDetails, [column]: details } };
        writeStored(storageKey, next);
        return next;
      }),
    [storageKey]
  );

  const handleViewTypeChange = useCallback((viewType: TableViewTogglesViewType) => update({ viewType }), [update]);

  return { ...settings, handleColumnChange, handleColumnDetailsChange, handleViewTypeChange };
};
